import React, { useState, useEffect } from 'react';
import { userService } from '../../services/userService';

const UserForm = ({ onSubmit, onCancel, initialData = null, isEditMode = false }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    secondLastName: '',
    rut: '',
    email: '',
    phone: '',
    region: '',
    commune: '',
    street: '',
    streetNumber: '',
    apartment: '',
    role: '',
    password: '',
    confirmPassword: '',
    status: 'activo',
    students: [] // Alumnos vinculados (para apoderados)
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [roles, setRoles] = useState([]);

  // Estados para el buscador de alumnos
  const [studentSearch, setStudentSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    // Cargar roles disponibles
    setRoles(userService.getRoles());

    // Si hay datos iniciales (modo edición), cargarlos
    if (initialData) {
      setFormData({
        firstName: initialData.firstName || '',
        lastName: initialData.lastName || '',
        secondLastName: initialData.secondLastName || '',
        rut: initialData.rut || '',
        email: initialData.email || '',
        phone: initialData.phone || '',
        region: initialData.region || '',
        commune: initialData.commune || '',
        street: initialData.street || '',
        streetNumber: initialData.streetNumber || '',
        apartment: initialData.apartment || '',
        role: initialData.role || '',
        status: initialData.status || 'activo',
        students: initialData.students || [],
        password: '',
        confirmPassword: ''
      });
    }
  }, [initialData]);

  // Buscar alumnos cuando cambie el texto de búsqueda
  useEffect(() => {
    const searchStudents = async () => {
      if (studentSearch.trim().length < 2) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        // Buscar usuarios con rol 'alumno'
        const response = await userService.getUsers({
          search: studentSearch,
          role: 'alumno',
          status: 'activo'
        });

        if (response.success) {
          // Filtrar los que ya están vinculados
          const filtered = response.data.filter(
            student => !formData.students.some(s => s.id === student.id)
          );
          setSearchResults(filtered);
        }
      } catch (error) {
        console.error('Error al buscar alumnos:', error);
      } finally {
        setIsSearching(false);
      }
    };

    const debounceTimer = setTimeout(searchStudents, 300);
    return () => clearTimeout(debounceTimer);
  }, [studentSearch, formData.students]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Limpiar error del campo al escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateRUT = (rut) => {
    // Validación básica de formato RUT chileno
    const rutPattern = /^[0-9]{1,2}\.[0-9]{3}\.[0-9]{3}-[0-9kK]{1}$/;
    return rutPattern.test(rut);
  };

  const validateEmail = (email) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  };

  const handleAddStudent = (student) => {
    setFormData(prev => ({
      ...prev,
      students: [...prev.students, student]
    }));
    setStudentSearch('');
    setSearchResults([]);
  };

  const handleRemoveStudent = (studentId) => {
    setFormData(prev => ({
      ...prev,
      students: prev.students.filter(s => s.id !== studentId)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Validar nombres
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Los nombres son requeridos';
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'Los nombres deben tener al menos 2 caracteres';
    }

    // Validar primer apellido
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'El primer apellido es requerido';
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = 'El primer apellido debe tener al menos 2 caracteres';
    }

    // Segundo apellido es opcional, pero si se proporciona debe tener al menos 2 caracteres
    if (formData.secondLastName.trim() && formData.secondLastName.trim().length < 2) {
      newErrors.secondLastName = 'El segundo apellido debe tener al menos 2 caracteres';
    }

    // Validar RUT
    if (!formData.rut.trim()) {
      newErrors.rut = 'El RUT es requerido';
    } else if (!validateRUT(formData.rut)) {
      newErrors.rut = 'El RUT no es válido (formato: 12.345.678-9)';
    }

    // Validar email
    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'El email no es válido';
    }

    // Validar teléfono
    if (!formData.phone.trim()) {
      newErrors.phone = 'El teléfono es requerido';
    }

    // Validar dirección
    if (!formData.region.trim()) {
      newErrors.region = 'La región es requerida';
    }

    if (!formData.commune.trim()) {
      newErrors.commune = 'La comuna es requerida';
    }

    if (!formData.street.trim()) {
      newErrors.street = 'La calle es requerida';
    }

    if (!formData.streetNumber.trim()) {
      newErrors.streetNumber = 'El número es requerido';
    }

    // Departamento es opcional, no requiere validación

    // Validar rol
    if (!formData.role) {
      newErrors.role = 'Debes seleccionar un rol';
    }

    // Validar contraseña (solo en modo creación o si se está cambiando)
    if (!isEditMode) {
      if (!formData.password) {
        newErrors.password = 'La contraseña es requerida';
      } else if (formData.password.length < 6) {
        newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
      }

      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Debes confirmar la contraseña';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Las contraseñas no coinciden';
      }
    } else if (formData.password || formData.confirmPassword) {
      // En modo edición, solo validar si se está intentando cambiar la contraseña
      if (formData.password && formData.password.length < 6) {
        newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Las contraseñas no coinciden';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Preparar datos para enviar
      const dataToSubmit = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        secondLastName: formData.secondLastName.trim(),
        // Crear nombre completo combinando los campos
        name: `${formData.firstName.trim()} ${formData.lastName.trim()} ${formData.secondLastName.trim()}`.trim(),
        rut: formData.rut.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        region: formData.region.trim(),
        commune: formData.commune.trim(),
        street: formData.street.trim(),
        streetNumber: formData.streetNumber.trim(),
        apartment: formData.apartment.trim(),
        // Crear dirección completa para compatibilidad
        address: `${formData.street.trim()} ${formData.streetNumber.trim()}${formData.apartment ? ', Depto. ' + formData.apartment.trim() : ''}, ${formData.commune.trim()}, ${formData.region.trim()}`,
        role: formData.role,
        status: formData.status,
        permissions: userService.getPermissionsByRole(formData.role)
      };

      // Solo incluir contraseña si se proporcionó
      if (formData.password) {
        dataToSubmit.password = formData.password;
      }

      // Si es apoderado, incluir los alumnos vinculados
      if (formData.role === 'apoderado') {
        dataToSubmit.students = formData.students.map(s => s.id);
      }

      await onSubmit(dataToSubmit);
    } catch (error) {
      console.error('Error al enviar formulario:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-xl font-bold text-gray-900">
          {isEditMode ? 'Editar Usuario' : 'Nuevo Usuario'}
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          {isEditMode
            ? 'Actualiza la información del usuario'
            : 'Completa los datos para crear un nuevo usuario'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-8">
        {/* Información Personal */}
        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <div className="flex items-center mb-5">
            <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h3 className="ml-3 text-lg font-semibold text-gray-900">
              Información Personal
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Nombres */}
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                Nombres *
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.firstName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Ej: Juan Carlos"
              />
              {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
            </div>

            {/* Primer Apellido */}
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                Primer Apellido *
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.lastName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Ej: Pérez"
              />
              {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
            </div>

            {/* Segundo Apellido */}
            <div>
              <label htmlFor="secondLastName" className="block text-sm font-medium text-gray-700 mb-1">
                Segundo Apellido
              </label>
              <input
                type="text"
                id="secondLastName"
                name="secondLastName"
                value={formData.secondLastName}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.secondLastName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Ej: González"
              />
              {errors.secondLastName && <p className="text-red-500 text-xs mt-1">{errors.secondLastName}</p>}
            </div>

            {/* RUT */}
            <div>
              <label htmlFor="rut" className="block text-sm font-medium text-gray-700 mb-1">
                RUT *
              </label>
              <input
                type="text"
                id="rut"
                name="rut"
                value={formData.rut}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.rut ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="12.345.678-9"
              />
              {errors.rut && <p className="text-red-500 text-xs mt-1">{errors.rut}</p>}
            </div>
          </div>
        </div>

        {/* Información de Contacto */}
        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <div className="flex items-center mb-5">
            <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="ml-3 text-lg font-semibold text-gray-900">
              Información de Contacto
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Email */}
            <div className="md:col-span-2">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="usuario@educapp.cl"
              />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>

            {/* Teléfono */}
            <div className="md:col-span-2">
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Teléfono *
              </label>
              <input
                type="text"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.phone ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="+56 9 1234 5678"
              />
              {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
            </div>

            {/* Región */}
            <div>
              <label htmlFor="region" className="block text-sm font-medium text-gray-700 mb-1">
                Región *
              </label>
              <input
                type="text"
                id="region"
                name="region"
                value={formData.region}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.region ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Ej: Región Metropolitana"
              />
              {errors.region && <p className="text-red-500 text-xs mt-1">{errors.region}</p>}
            </div>

            {/* Comuna */}
            <div>
              <label htmlFor="commune" className="block text-sm font-medium text-gray-700 mb-1">
                Comuna *
              </label>
              <input
                type="text"
                id="commune"
                name="commune"
                value={formData.commune}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.commune ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Ej: Santiago"
              />
              {errors.commune && <p className="text-red-500 text-xs mt-1">{errors.commune}</p>}
            </div>

            {/* Calle */}
            <div>
              <label htmlFor="street" className="block text-sm font-medium text-gray-700 mb-1">
                Calle *
              </label>
              <input
                type="text"
                id="street"
                name="street"
                value={formData.street}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.street ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Ej: Av. Libertador Bernardo O'Higgins"
              />
              {errors.street && <p className="text-red-500 text-xs mt-1">{errors.street}</p>}
            </div>

            {/* Número */}
            <div>
              <label htmlFor="streetNumber" className="block text-sm font-medium text-gray-700 mb-1">
                Número *
              </label>
              <input
                type="text"
                id="streetNumber"
                name="streetNumber"
                value={formData.streetNumber}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.streetNumber ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Ej: 1234"
              />
              {errors.streetNumber && <p className="text-red-500 text-xs mt-1">{errors.streetNumber}</p>}
            </div>

            {/* Departamento (opcional) */}
            <div className="md:col-span-2">
              <label htmlFor="apartment" className="block text-sm font-medium text-gray-700 mb-1">
                Departamento <span className="text-gray-500 text-xs">(opcional)</span>
              </label>
              <input
                type="text"
                id="apartment"
                name="apartment"
                value={formData.apartment}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Ej: 305"
              />
            </div>
          </div>
        </div>

        {/* Información del Sistema */}
        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <div className="flex items-center mb-5">
            <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h3 className="ml-3 text-lg font-semibold text-gray-900">
              Información del Sistema
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Rol */}
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                Rol *
              </label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.role ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Seleccionar rol</option>
                {roles.map(role => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
              {errors.role && <p className="text-red-500 text-xs mt-1">{errors.role}</p>}
            </div>

            {/* Estado */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                Estado *
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="activo">Activo</option>
                <option value="inactivo">Inactivo</option>
              </select>
            </div>
          </div>
        </div>

        {/* Vinculación de Alumnos (solo para apoderados) */}
        {formData.role === 'apoderado' && (
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <div className="flex items-center mb-5">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <h3 className="ml-3 text-lg font-semibold text-gray-900">
                Alumnos Vinculados
              </h3>
            </div>

            {/* Buscador de alumnos */}
            <div className="relative">
              <label htmlFor="studentSearch" className="block text-sm font-medium text-gray-700 mb-1">
                Buscar Alumno
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="studentSearch"
                  value={studentSearch}
                  onChange={(e) => setStudentSearch(e.target.value)}
                  placeholder="Buscar por nombre o RUT..."
                  className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <svg
                  className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>

              {/* Resultados de búsqueda */}
              {(isSearching || searchResults.length > 0) && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {isSearching ? (
                    <div className="p-4 text-center text-sm text-gray-500">
                      Buscando...
                    </div>
                  ) : searchResults.length > 0 ? (
                    searchResults.map(student => (
                      <button
                        key={student.id}
                        type="button"
                        onClick={() => handleAddStudent(student)}
                        className="w-full px-4 py-3 hover:bg-gray-50 flex items-center justify-between border-b border-gray-100 last:border-b-0 transition-colors"
                      >
                        <div className="flex items-center">
                          <img
                            src={student.avatar}
                            alt={student.name}
                            className="w-8 h-8 rounded-full"
                          />
                          <div className="ml-3 text-left">
                            <div className="text-sm font-medium text-gray-900">{student.name}</div>
                            <div className="text-xs text-gray-500">{student.rut}</div>
                          </div>
                        </div>
                        <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                      </button>
                    ))
                  ) : studentSearch.trim().length >= 2 ? (
                    <div className="p-4 text-center text-sm text-gray-500">
                      No se encontraron alumnos
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            {/* Lista de alumnos vinculados */}
            {formData.students.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Alumnos ({formData.students.length})
                </p>
                <div className="space-y-2">
                  {formData.students.map(student => (
                    <div
                      key={student.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex items-center">
                        <img
                          src={student.avatar}
                          alt={student.name}
                          className="w-8 h-8 rounded-full"
                        />
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">{student.name}</div>
                          <div className="text-xs text-gray-500">{student.rut}</div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveStudent(student.id)}
                        className="text-red-600 hover:text-red-800 p-1 rounded-lg hover:bg-red-50 transition-colors"
                        title="Eliminar vinculación"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {formData.students.length === 0 && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg mt-4">
                <p className="text-sm text-yellow-800">
                  No hay alumnos vinculados. Usa el buscador para agregar alumnos a este apoderado.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Contraseña */}
        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <div className="flex items-center mb-5">
            <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-semibold text-gray-900">
                {isEditMode ? 'Cambiar Contraseña' : 'Contraseña'}
              </h3>
              {isEditMode && (
                <p className="text-xs text-gray-500 mt-0.5">
                  Dejar en blanco para mantener la contraseña actual
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Contraseña */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                {isEditMode ? 'Nueva Contraseña' : 'Contraseña *'}
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.password ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder={isEditMode ? 'Dejar en blanco para mantener' : 'Mínimo 6 caracteres'}
              />
              {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
            </div>

            {/* Confirmar contraseña */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                {isEditMode ? 'Confirmar Nueva Contraseña' : 'Confirmar Contraseña *'}
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Confirmar contraseña"
              />
              {errors.confirmPassword && (
                <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
              )}
            </div>
          </div>
        </div>

        {/* Botones de acción */}
        <div className="flex justify-end space-x-3 pt-8">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Guardando...' : isEditMode ? 'Actualizar' : 'Crear Usuario'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserForm;
